export type SortableTeam = {
  team_id: number | null;
  team_name?: string | null;
};

const teamNameCollator = new Intl.Collator(["ko", "en"], {
  numeric: true,
  sensitivity: "base",
});

const TEAM_NAME_GROUP_RANK = {
  ko: 0,
  en: 1,
  number: 2,
  other: 3,
} as const;

const getTeamNameGroupRank = (name: string) => {
  const first = name.trim().charAt(0);
  if (!first) return TEAM_NAME_GROUP_RANK.other;
  if (/[\uac00-\ud7a3]/.test(first)) return TEAM_NAME_GROUP_RANK.ko;
  if (/[A-Za-z]/.test(first)) return TEAM_NAME_GROUP_RANK.en;
  if (/[0-9]/.test(first)) return TEAM_NAME_GROUP_RANK.number;
  return TEAM_NAME_GROUP_RANK.other;
};

const isTemporaryTeam = (team: SortableTeam) =>
  typeof team.team_id === "number" && team.team_id < 0;

const isUnassignedTeam = (team: SortableTeam) => team.team_id == null;

const teamIdSortValue = (team: SortableTeam) =>
  typeof team.team_id === "number" ? team.team_id : Number.MAX_SAFE_INTEGER;

export const compareTeamsByName = <T extends SortableTeam>(a: T, b: T) => {
  const aIsUnassigned = isUnassignedTeam(a);
  const bIsUnassigned = isUnassignedTeam(b);
  if (aIsUnassigned !== bIsUnassigned) return aIsUnassigned ? -1 : 1;

  const aIsTemp = isTemporaryTeam(a);
  const bIsTemp = isTemporaryTeam(b);
  if (aIsTemp !== bIsTemp) return aIsTemp ? 1 : -1;

  const aName = a.team_name ?? "";
  const bName = b.team_name ?? "";
  const rankDiff = getTeamNameGroupRank(aName) - getTeamNameGroupRank(bName);
  if (rankDiff !== 0) return rankDiff;

  const nameCompare = teamNameCollator.compare(aName, bName);
  if (nameCompare !== 0) return nameCompare;

  return teamIdSortValue(a) - teamIdSortValue(b);
};

export const sortTeamsByName = <T extends SortableTeam>(teams: readonly T[]) =>
  [...teams].sort(compareTeamsByName);

export const sortTeamGroupsByTeamName = <
  T extends {
    teams: SortableTeam;
  },
>(
  teamGroups: readonly T[]
) => [...teamGroups].sort((a, b) => compareTeamsByName(a.teams, b.teams));
